import type { ForwardedRef } from 'react';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { DATETIME_UNIT } from '@ricoh/ria.client.core/constants/core/unit';
import { ClientError } from '@ricoh/ria.client.core/errors';
import { useFirstRenderLayoutEffect } from '@ricoh/ria.client.core/hooks/app/lifecycle';
import { isNotNull, isNull } from '@ricoh/ria.client.core/utils/data/object';

import type { CounterHooks, CounterProps, CounterRef } from './_.type';

/**
 * Use Counter hooks.
 *
 * @template TValue Counter value type.
 * @param {CounterProps} _props Counter props.
 * @param {CounterRef} ref Counter ref.
 *
 * @returns {CounterHooks} Counter hooks.
 */
export default function useCounterHooks<TValue>(_props: CounterProps<TValue>, ref: ForwardedRef<CounterRef<TValue>>): CounterHooks<TValue> {
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const [value, setValue] = useState<TValue | undefined>(undefined);

  const stop = useCallback<Func>(() => {
    if (isNull(intervalRef.current)) {
      return;
    }

    setValue(() => undefined);

    // * Clear interval timer
    clearInterval(intervalRef.current);
    intervalRef.current = undefined;
  }, []);

  const count = useCallback<CounterRef<TValue>['count']>(
    ({ initialValue, update, predicate, onDone, delay = 1 * DATETIME_UNIT.SECOND }) => {
      if (isNotNull(intervalRef.current)) {
        throw new ClientError({
          module: 'component',
          message: 'Counter is counting',
          cause: {
            interval: intervalRef.current,
          },
        });
      }

      let currentValue = initialValue;
      if (predicate(currentValue)) {
        setValue(() => currentValue);
      } else {
        return;
      }

      intervalRef.current = setInterval(() => {
        if (predicate(currentValue)) {
          currentValue = update(currentValue);
          setValue(() => currentValue);
          return;
        }

        stop();
        onDone?.();
      }, delay);
    },
    [stop],
  );

  const cancel = useCallback<CounterRef<TValue>['cancel']>(() => {
    stop();
  }, [stop]);

  // * Stop counter when Counter unmount
  useFirstRenderLayoutEffect(() => stop);

  useImperativeHandle(ref, () => ({
    count,
    cancel,
  }));

  return useMemo<CounterHooks<TValue>>(
    () => ({
      refs: {},

      states: {
        value,
      },

      events: {},
    }),
    [value],
  );
}
